import { Injectable, InjectionToken } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

export interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt: number;
  /**
   * The scope the IdP actually granted, which can be narrower than the one requested.
   * It is echoed back on refresh — asking for more than was granted is an `invalid_scope`
   * error, asking for less silently narrows the new access token.
   */
  scope?: string;
}

export interface PendingLogin {
  verifier: string;
  state: string;
  /**
   * Must survive the redirect: without it the `nonce` claim of the returned ID token
   * cannot be compared against the one that was sent, which OIDC requires whenever a
   * nonce was part of the authorization request.
   */
  nonce: string;
}

export interface TokenStorage {
  load(): Promise<StoredTokens | null>;
  save(tokens: StoredTokens): Promise<void>;
  clear(): Promise<void>;
  loadPendingLogin(): Promise<PendingLogin | null>;
  savePendingLogin(pending: PendingLogin): Promise<void>;
  clearPendingLogin(): Promise<void>;
}

export const TOKEN_STORAGE = new InjectionToken<TokenStorage>('TOKEN_STORAGE');

const TOKENS_KEY = 'oidc_tokens';
const PENDING_KEY = 'oidc_pending_login';

@Injectable()
export class PreferencesTokenStorage implements TokenStorage {
  async load(): Promise<StoredTokens | null> {
    const { value } = await Preferences.get({ key: TOKENS_KEY });
    return value ? (JSON.parse(value) as StoredTokens) : null;
  }

  async save(tokens: StoredTokens): Promise<void> {
    await Preferences.set({ key: TOKENS_KEY, value: JSON.stringify(tokens) });
  }

  async clear(): Promise<void> {
    await Preferences.remove({ key: TOKENS_KEY });
  }

  async loadPendingLogin(): Promise<PendingLogin | null> {
    const { value } = await Preferences.get({ key: PENDING_KEY });
    return value ? (JSON.parse(value) as PendingLogin) : null;
  }

  async savePendingLogin(pending: PendingLogin): Promise<void> {
    await Preferences.set({ key: PENDING_KEY, value: JSON.stringify(pending) });
  }

  async clearPendingLogin(): Promise<void> {
    await Preferences.remove({ key: PENDING_KEY });
  }
}
